import { customAuth } from "../standardAuthPatterns";
import logo from "./logo.svg";
import { defineProduct, productVariant } from "../types";

export const akamai = defineProduct({
  name: "Akamai",
  tagLine: "Cloud delivery and security platform",
  logo,
  tags: ["security"],
  variants: [
    productVariant({
      identifier: "akamai",
      authLabel: "EdgeGrid credentials",
      protocol: "http",
      defaultUrlPattern: () => "https://[^/]*\\.luna\\.akamaiapis\\.net/.*",
      auth: (app) =>
        customAuth("akamaiEdgeGrid", {
          clientToken: app.secret.clientToken,
          clientSecret: app.secret.clientSecret,
          accessToken: app.secret.accessToken,
        }),
      steps: [
        {
          title: "Create API credentials",
          items: [
            {
              kind: "markdown",
              content:
                "In [Akamai Control Center](https://control.akamai.com), go to **Identity & Access Management > API Users** and create API client credentials with the appropriate permissions.",
            },
          ],
        },
        {
          title: "Enter your Akamai credentials",
          items: [
            {
              kind: "hostname",
              label: "Host",
              placeholder: "akab-xxxxxxxx.luna.akamaiapis.net",
              required: true,
              target: { kind: "configuration", field: "host" },
              helperText: "Your Akamai API hostname from the .edgerc file.",
            },
            {
              kind: "input",
              label: "Client token",
              required: true,
              target: { kind: "secrets", field: "clientToken" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "secrets", field: "clientSecret" },
            },
            {
              kind: "input",
              label: "Access token",
              required: true,
              target: { kind: "secrets", field: "accessToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ host }) =>
          `https://${host}/diagnostic-tools/v2/ghost-locations/available`,
      },
      publicEnvVars: ({ host }) => ({
        AKAMAI_URL: host ? `https://${host}` : "",
      }),
    }),
    productVariant({
      identifier: "akamaiClientList",
      authLabel: "EdgeGrid credentials (Client Lists scope)",
      protocol: "http",
      defaultUrlPattern: ({ host }) =>
        host
          ? `https://${host.replace(/\./g, "\\.")}/.*`
          : "https://[^/]*\\.luna\\.akamaiapis\\.net/.*",
      auth: (app) =>
        customAuth("akamaiEdgeGrid", {
          clientToken: app.secret.clientToken,
          clientSecret: app.secret.clientSecret,
          accessToken: app.secret.accessToken,
        }),
      steps: [
        {
          title: "Create API credentials",
          items: [
            {
              kind: "markdown",
              content:
                "Follow the [Akamai Identity & Access Management docs](https://techdocs.akamai.com/iam/docs/about-id-access-mgmt-app) to create API client credentials with access to the **Client Lists** API.",
            },
          ],
        },
        {
          title: "Enter your Akamai credentials",
          items: [
            {
              kind: "input",
              label: "Host",
              placeholder: "akab-xxxxxxxx.luna.akamaiapis.net",
              required: true,
              target: { kind: "configuration", field: "host" },
              helperText: "Your Akamai API hostname from the .edgerc file.",
            },
            {
              kind: "input",
              label: "Client token",
              required: true,
              target: { kind: "secrets", field: "clientToken" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "secrets", field: "clientSecret" },
            },
            {
              kind: "input",
              label: "Access token",
              required: true,
              target: { kind: "secrets", field: "accessToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ host }) => `https://${host}/client-list/v1/lists`,
      },
    }),
  ],
});
